import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Star, Trophy } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useFont } from '../contexts/FontContext';
import { useSubscription } from '../hooks/useSubscription';
import BackButton from '../components/BackButton';
import { getModules } from '../mockApi';
import type { Module } from '../types';

export default function Courses() {
  const { theme } = useTheme();
  const { font } = useFont();
  const { hasActiveSubscription } = useSubscription();
  const navigate = useNavigate();
  const [modules, setModules] = useState<Module[]>([]);

  useEffect(() => {
    getModules().then(modules => {
      // Filter out VAE modules from the regular list
      const regularModules = modules.filter(m => !m.id.startsWith('VAE-'));
      setModules(regularModules);
    });
  }, []);

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: font.family }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <BackButton />
        </div>
        <h1 className={`text-3xl font-bold text-${theme.text} mb-8`}>
          Modules de formation
        </h1>

        <div className="mb-12">
          <div className="aspect-video w-full rounded-lg overflow-hidden shadow-lg">
            <iframe
              src="https://player.vimeo.com/video/1106418955?h=b3f1263b10"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <div
            onClick={() => {
              if (hasActiveSubscription()) {
                navigate(`/cours/VAE-1`);
              } else {
                navigate('/tarifs');
              }
            }}
            className={`bg-white rounded-lg shadow-sm transition-shadow p-6 border border-gray-100 relative ${
              hasActiveSubscription() 
                ? 'hover:shadow-md cursor-pointer' 
                : 'cursor-pointer opacity-75'
            }`}
          >
            {/* Content */}
            <div className="flex items-start">
              <div className={`p-3 rounded-lg bg-${theme.secondary} mr-6`}>
                <Star className={`h-6 w-6 text-${theme.text}`} />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold mb-2">
                  Le métier d'Auxiliaire de puériculture
                </h2>
                <p className="text-gray-600">
                  Découvrez les missions, les compétences et le quotidien d'un auxiliaire de puériculture.
                </p>
              </div>
            </div>

            {/* Lock overlay for non-subscribed users */}
            {!hasActiveSubscription() && (
              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 rounded-lg">
                <div className="text-center">
                  <div className="w-8 h-8 mx-auto mb-2 bg-red-100 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-xs text-red-600 font-medium">Contenu premium</p>
                </div>
              </div>
            )}
          </div>

          <div
            onClick={() => {
              if (hasActiveSubscription()) {
                navigate(`/cours/VAE-2`);
              } else {
                navigate('/tarifs');
              }
            }}
            className={`bg-white rounded-lg shadow-sm transition-shadow p-6 border border-gray-100 relative ${
              hasActiveSubscription() 
                ? 'hover:shadow-md cursor-pointer' 
                : 'cursor-pointer opacity-75'
            }`}
          >
            {/* Content */}
            <div className="flex items-start">
              <div className={`p-3 rounded-lg bg-${theme.secondary} mr-6`}>
                <Trophy className={`h-6 w-6 text-${theme.text}`} />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold mb-2">
                  La VAE
                </h2>
                <p className="text-gray-600">
                  Comprendre le processus de Validation des Acquis de l'Expérience et ses étapes clés.
                </p>
              </div>
            </div>

            {/* Lock overlay for non-subscribed users */}
            {!hasActiveSubscription() && (
              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 rounded-lg">
                <div className="text-center">
                  <div className="w-8 h-8 mx-auto mb-2 bg-red-100 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-xs text-red-600 font-medium">Contenu premium</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {modules.map((module) => (
            <div
              key={module.id}
              onClick={() => navigate(`/cours/${module.code}`)}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-6 border border-gray-100 cursor-pointer"
            >
              <div className="flex items-start">
                <div className={`p-3 rounded-lg bg-${theme.secondary} mr-6`}>
                  <BookOpen className={`h-6 w-6 text-${theme.text}`} />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-semibold mb-2">
                    <span className={`text-${theme.text} mr-2`}>{module.code}</span>
                    {module.title}
                  </h2>
                  <div
                    className="markdown-content text-sm"
                    dangerouslySetInnerHTML={{
                      __html: module.description
                        .split('\n')
                        .map(line => line.trim())
                        .map(line => {
                          if (line.startsWith('* ')) {
                            return `<li>${line.substring(2)}</li>`;
                          }
                          return line;
                        })
                        .join('\n')
                        .replace(/(<li>.*?<\/li>\n*)+/g, '<ul>$&</ul>')
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}